// Section config + renderers for the Article DPP panel.
// Each entry exposes `key`, `header`, and `render(dpp)` returning either a
// React node (formatted value) or null (which lets DppSection fall back to
// the "Not available" / "None" placeholder defined inline below).
//
// Formats follow task2 spec; data shapes come from DPP_DOC.md → dpp_compliance.

import React from "react";
import { Typography } from "@material-ui/core";
import supplyMap from "./SupplyMap";

const round2 = (n) => Math.round(n * 100) / 100;

const PlaceholderNone = ({ label = "None" }) => (
  <Typography variant="body2" color="textSecondary">
    {label}
  </Typography>
);

const PlaceholderUnavailable = () => <PlaceholderNone label="Not available" />;

const joinDots = (parts) => parts.filter(Boolean).join(" · ");

const fiberComposition = (dpp) => {
  const list = dpp?.fiber_composition;
  if (!Array.isArray(list) || list.length === 0) return null;
  const text = joinDots(
    list.map((f) => `${f.fiber_type} ${round2(f.percentage)}%`)
  );
  return <Typography variant="body2">{text}</Typography>;
};

const fiberSubContent = (list, percentageKey) => {
  if (!Array.isArray(list) || list.length === 0) return <PlaceholderNone />;
  const filtered = list.filter((f) => Number(f[percentageKey]) > 0);
  if (filtered.length === 0) return <PlaceholderNone />;
  const total = filtered.reduce((acc, f) => acc + Number(f[percentageKey] || 0), 0);
  const text = joinDots([
    `${round2(total)}% total`,
    ...filtered.map((f) => `${f.fiber_type} ${round2(f[percentageKey])}%`),
  ]);
  return <Typography variant="body2">{text}</Typography>;
};

const recycledContent = (dpp) =>
  fiberSubContent(dpp?.recycled_content_by_fiber, "recycled_percentage");

const organicContent = (dpp) =>
  fiberSubContent(dpp?.organic_content_by_fiber, "organic_percentage");

const biobasedContent = (dpp) =>
  fiberSubContent(dpp?.biobased_content_by_fiber, "biobased_percentage");

const productWeight = (dpp) => {
  const v = dpp?.product_weight_kg;
  if (v === null || v === undefined) return <PlaceholderUnavailable />;
  return <Typography variant="body2">{`${round2(v)} kg`}</Typography>;
};

const nonTextileComponents = (dpp) => {
  const list = dpp?.non_textile_components;
  if (!Array.isArray(list) || list.length === 0) return <PlaceholderNone />;
  return (
    <Typography variant="body2" component="div">
      {list.map((c, i) => {
        const name = c.name || c.component_type || c.material || "—";
        const quantity = c.quantity ?? c.qty;
        const unit = c.unit || c.units;
        const amount =
          quantity === null || quantity === undefined
            ? null
            : `${round2(Number(quantity))}${unit ? ` ${unit}` : ""}`;
        return <div key={i}>{joinDots([name, amount])}</div>;
      })}
    </Typography>
  );
};

const reusedContent = (dpp) => {
  const v = dpp?.reused_content;
  if (!v) return <PlaceholderNone />;
  const pct = Number(v.reused_content_percentage || 0);
  if (pct <= 0) return <PlaceholderNone />;
  const components = Array.isArray(v.reused_components) ? v.reused_components : [];
  const text = joinDots([
    `${round2(pct)}%`,
    ...components.map((c) => `${c.material_name} ${round2(c.percentage)}%`),
  ]);
  return <Typography variant="body2">{text}</Typography>;
};

const reachDeclaration = (dpp) => {
  // The field is omitted when there are no hazardous substances → "None declared".
  const declaration = dpp?.reach_declaration;
  const substances = declaration?.substances;
  if (!Array.isArray(substances) || substances.length === 0) {
    return <PlaceholderNone label="None declared" />;
  }
  const text = joinDots(
    substances.map((s) => {
      const ec = s.ec_number ? `EC ${s.ec_number}` : null;
      const cas = s.cas_number ? `CAS ${s.cas_number}` : null;
      const inner = joinDots([ec, cas].filter(Boolean));
      return inner ? `${s.short_name || s.name} (${inner})` : s.short_name || s.name;
    })
  );
  return <Typography variant="body2">{text}</Typography>;
};

const productionWaste = (dpp) => {
  const w = dpp?.production_waste;
  const value = w?.production_waste_kg;
  if (!w || value === null || value === undefined) return <PlaceholderUnavailable />;
  return <Typography variant="body2">{`${round2(value)} ${w.unit || "kg"}`}</Typography>;
};

const carbonFootprint = (dpp) => {
  const cf = dpp?.carbon_footprint;
  if (!cf || cf.value === null || cf.value === undefined) return <PlaceholderUnavailable />;
  return (
    <Typography variant="body2">
      {`${round2(cf.value)} ${cf.unit || "kg CO2eq"}`}
    </Typography>
  );
};

const waterFootprint = (dpp) => {
  const wf = dpp?.water_footprint;
  if (!wf || wf.value === null || wf.value === undefined) return <PlaceholderUnavailable />;
  return (
    <Typography variant="body2">
      {`${round2(wf.value)} ${wf.unit || "m3 world-eq"}`}
    </Typography>
  );
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const euRegistry = (_dpp, article) => {
  const id = article?.euRegistryId;
  const hash = article?.dppVersionHash;
  const registeredAt = article?.dppRegisteredAt;
  if (!id && !hash && !registeredAt) return <PlaceholderNone label="Not registered" />;
  return (
    <Typography variant="body2" component="div">
      <div>EU Registry ID: {id || "—"}</div>
      <div style={{ wordBreak: "break-all" }}>Version hash: {hash || "—"}</div>
      <div>Registered at: {formatDate(registeredAt) || "—"}</div>
    </Typography>
  );
};

// ─── DPP - Care & Use, Textile Durability, End of Life (IT-3100) ──────────────

// care_instructions: Array<{ iso_code, description }>. Empty/missing → dash.
const careInstructions = (dpp) => {
  const list = dpp?.care_instructions;
  if (!Array.isArray(list) || list.length === 0) return <PlaceholderNone label="—" />;
  return (
    <Typography variant="body2" component="div">
      {list.map((c, i) => (
        <div key={c.iso_code || i}>
          {c.iso_code}
          {c.description ? ` — ${c.description}` : ""}
        </div>
      ))}
    </Typography>
  );
};

// textile_durability: { colour_fastness, pilling_resistance, declared_service_life_years }.
// Only present if at least one value exists; dash if all null/missing.
const textileDurability = (dpp) => {
  const d = dpp?.textile_durability;
  const cf = d?.colour_fastness;
  const pr = d?.pilling_resistance;
  const sl = d?.declared_service_life_years;
  const isEmpty = (v) => v === null || v === undefined;
  if (!d || (isEmpty(cf) && isEmpty(pr) && isEmpty(sl))) return <PlaceholderNone label="—" />;
  return (
    <Typography variant="body2" component="div">
      <div>Colour fastness: {isEmpty(cf) ? "—" : cf}</div>
      <div>Pilling resistance: {isEmpty(pr) ? "—" : pr}</div>
      <div>Declared service life: {isEmpty(sl) ? "—" : `${sl} years`}</div>
    </Typography>
  );
};

// end_of_life: { recommended: "donate", collection_point_url }. Always present.
// collection_point_url admite URL o texto libre: solo se linkea si es http(s).
const endOfLife = (dpp) => {
  const eol = dpp?.end_of_life;
  if (!eol) return <PlaceholderNone label="—" />;
  const value = eol.collection_point_url;
  const isHttpUrl = typeof value === "string" && /^https?:\/\//i.test(value.trim());
  return (
    <Typography variant="body2" component="div">
      <div>Recommended: {eol.recommended || "donate"}</div>
      <div style={{ wordBreak: "break-all" }}>
        Collection point:{" "}
        {value ? (
          isHttpUrl ? (
            <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
          ) : (
            value
          )
        ) : (
          "—"
        )}
      </div>
    </Typography>
  );
};

const sections = [
  { key: "eu_registry", header: "EU Registry", render: euRegistry },
  { key: "fiber_composition", header: "Fiber Composition", render: fiberComposition },
  { key: "recycled_content", header: "Recycled Content", render: recycledContent },
  { key: "organic_content", header: "Organic Content", render: organicContent },
  { key: "biobased_content", header: "Biobased Content", render: biobasedContent },
  { key: "product_weight_kg", header: "Product Weight", render: productWeight },
  { key: "non_textile_components", header: "Non-Textile Components", render: nonTextileComponents },
  { key: "reused_content", header: "Reused / Second-Life Content", render: reusedContent },
  { key: "reach_declaration", header: "REACH Declaration", render: reachDeclaration },
  { key: "production_waste", header: "Unrecoverable Production Waste", render: productionWaste },
  { key: "carbon_footprint", header: "Carbon Footprint", render: carbonFootprint },
  { key: "water_footprint", header: "Water Footprint", render: waterFootprint },
  { key: "care_instructions", header: "Care Instructions", render: careInstructions },
  { key: "textile_durability", header: "Textile Durability", render: textileDurability },
  { key: "end_of_life", header: "End of Life", render: endOfLife },
  { key: "supply_map", header: "Supply Map", render: supplyMap },
];

export default sections;
